import React from 'react';
import ReactDOMServer from 'react-dom/server';
import { BrokerClientApi, DrawApi } from '@apply/api';
import { DrawType } from '@apply/constants';
import { EsignModal } from '../../../esign';
import { useState } from 'react';
import { ApplicationInsights } from '@microsoft/applicationinsights-web';

interface ISubmitButtonProps {
    draw: DrawApi.IDraw;
    currentApproval?: DrawApi.IDrawBudgetApproval;
    isEquityMet: boolean;
    applicationInsights: ApplicationInsights;
    borrowerName: string;
    latestDrawBorrowerRequestTotal: number;
    onDrawBorrowerSubmitted: (id: number) => void;
    showFeedback: (value: boolean) => void;
}

export const SubmitButton = ({
    draw,
    currentApproval,
    isEquityMet,
    applicationInsights,
    borrowerName,
    latestDrawBorrowerRequestTotal,
    onDrawBorrowerSubmitted,
    showFeedback,
}: ISubmitButtonProps): JSX.Element => {
    const drawApi = new DrawApi.DrawApi(applicationInsights);
    const brokerClientApi = new BrokerClientApi.BrokerClientApi(applicationInsights);

    const [isEsignOpen, set_isEsignOpen] = useState<boolean>(false);
    const [ipAddress, set_ipAddress] = useState<string>();

    const onSubmitClick = () => {
        brokerClientApi.getIpAddress().then((response) => {
            if (response.statusCode !== 200) return;

            set_ipAddress(response.result);
            set_isEsignOpen(true);
        });
    };

    const onConfirmAgreement = async (font) => {
        await drawApi.drawBorrowerSignature({
            drawId: draw.id,
            fontId: font.id,
            ipAddress: ipAddress,
        });

        onDrawBorrowerSubmitted(draw.id);

        set_isEsignOpen(false);

        if (draw.drawType === 2) {
            showFeedback(true);
        }
    };

    const onRejectAgreement = () => {
        set_isEsignOpen(false);
    };

    if ((draw.borrowerSubmitted && !draw.needsBorrowerSignature) || draw.drawType !== DrawType.Manual || draw.isFinalized)
        return <></>;

    if (!currentApproval) return <DisabledButton titleText='The budget is currently waiting for approval.' />;

    if (!isEquityMet) return <DisabledButton titleText='The equity requirement has not been met.' />;

    if (latestDrawBorrowerRequestTotal === 0) return <DisabledButton titleText='Please enter your budget request.' />;

    return (
        <>
            <button
                onClick={onSubmitClick}
                title='Submit Draw for review.'
                className='button primary'
                style={{ zIndex: 2001 }}
            >
                Submit
            </button>
            <EsignModal
                agreementHtml={manualAggreementString}
                isOpen={isEsignOpen}
                signatureText={borrowerName}
                cancelCallback={onRejectAgreement}
                confirmCallback={(font) => onConfirmAgreement(font)}
                ipAddress={ipAddress}
                rejectLabel='I DO NOT ACCEPT THIS AGREEMENT'
                acceptLabel='I ACCEPT THIS AGREEMENT'
            />
        </>
    );
};

const DisabledButton = ({ titleText }: { titleText: string }) => {
    return (
        <button
            disabled={true}
            title={titleText}
            className='button primary'
            style={{ zIndex: 2001 }}
        >
            Submit
        </button>
    );
};

const manualAggreementString = ReactDOMServer.renderToStaticMarkup(
    <div style={{ paddingLeft: 20, fontSize: 20 }}>
        <p style={{ fontSize: 24, fontWeight: 600 }}>I hereby certify the following:</p>
        <ol>
            <li style={{ paddingBottom: 25 }}>I have personally reviewed this draw submission and agree to draw funds.</li>
            <li style={{ paddingBottom: 25 }}>
                To the extent to which I am requesting funds for a line item, I am stating that work to be complete at the subject
                property unless otherwise specifically noted.
            </li>
            <li style={{ paddingBottom: 25 }}>
                All work stated to be complete herein is, in fact, complete and has been performed in acceptable standards of
                workmanship and quality.
            </li>
            <li style={{ paddingBottom: 25 }}>
                All work has been completed in accordance with the approved plans and specs of the project.
            </li>
            <li style={{ paddingBottom: 25 }}>
                All invoices and contracts covered by previous draws requests have been paid in full and no delinquent invoices
                exist.
            </li>
            <li>
                No unsatisfied mechanic's, workman's, or other similar liens have been placed on the property, and none are
                reasonably expected.
            </li>
        </ol>
    </div>
);
